// src/components/PeoplePlan/TheBench/TheBench.js
import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "./TheBench.css";

// Hooks
import useWorkflowData from "./hooks/useWorkflowData";
import useWorkflowFilters from "./hooks/useWorkflowFilters";
import useWorkflowSelection from "./hooks/useWorkflowSelection";
import useWorkflowSorting from "./hooks/useWorkflowSorting";

// Components
import WorkflowControls from "./components/WorkflowControls/WorkflowControls";
import WorkflowTable from "./components/WorkflowTable/WorkflowTable";
import WorkflowKanban from "./components/WorkflowKanban/WorkflowKanban";
import EmployeeDetails from "../../../pages/EmployeeDetails";

function TheBench({ viewMode: propViewMode, selectedMUM: propMUM, userRole, isIntegrated = false }) {
  const { workflowName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Use prop viewMode if integrated, otherwise internal state
  const [internalViewMode, setInternalViewMode] = useState("table");
  const viewMode = isIntegrated ? propViewMode : internalViewMode;
  
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Custom hooks - pass propMUM if integrated
  const {
    employees,
    loading,
    error,
    fetchEmployees,
    selectedMUM,  // eslint-disable-line no-unused-vars
    role,         // eslint-disable-line no-unused-vars
    employeeIds   // eslint-disable-line no-unused-vars
  } = useWorkflowData(location, workflowName, isIntegrated ? propMUM : null);

  const {
    filteredEmployees,
    selectedStatuses,
    columnFilters,
    kanbanFilters,
    handleStatusChange,
    handleColumnFilterChange,
    handleKanbanFilterChange,
    resetKanbanFilters,
    getUniqueValues
  } = useWorkflowFilters(employees);

  const {
    selectedEmployees,
    handleCheckboxChange,
    clearSelection
  } = useWorkflowSelection();

  const {
    sortedData,
    sortColumn,
    sortOrder,
    handleSort
  } = useWorkflowSorting(filteredEmployees);

  // Authentication check - skip if integrated (already checked in PeoplePlan)
  useEffect(() => {
    if (isIntegrated) return;
    
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/");
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (decodedToken.exp < currentTime) {
        localStorage.removeItem("authToken");
        navigate("/");
      }
    } catch (error) {
      console.error("Invalid token:", error);
      localStorage.removeItem("authToken");
      navigate("/");
    }
  }, [navigate, isIntegrated]);

  // View toggle handler - only used when not integrated
  const handleViewToggle = () => {
    if (!isIntegrated) {
      setInternalViewMode(prevMode => prevMode === "table" ? "kanban" : "table");
      clearSelection();
    }
  };

  // Employee selection handler
  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
  };

  // Log context for debugging (can be removed in production)
  useEffect(() => {
    if (selectedMUM || role || employeeIds.length > 0) {
      console.log("Workflow context - MUM:", selectedMUM, "Role:", role, "IDs:", employeeIds);
    }
  }, [selectedMUM, role, employeeIds]);

  if (error) {
    return (
      <div className={`the-bench ${isIntegrated ? 'integrated' : ''}`}>
        <div className="error-message">
          Error loading data: {error}
        </div>
      </div>
    );
  }

  return (
    <div className={`the-bench ${isIntegrated ? 'integrated' : ''}`}>
      {!isIntegrated && (
        <h1>
          The Bench: {workflowName ? decodeURIComponent(workflowName) : "All Active Workflows"}
          {viewMode === "kanban" && Object.values(kanbanFilters).some(v => v !== "all") && (
            <span className="filtered-count">
              ({filteredEmployees.length} of {employees.length})
            </span>
          )}
        </h1>
      )}

      <WorkflowControls
        viewMode={viewMode}
        onViewToggle={handleViewToggle}
        loading={loading}
        employees={employees}
        kanbanFilters={kanbanFilters}
        onKanbanFilterChange={handleKanbanFilterChange}
        onResetKanbanFilters={resetKanbanFilters}
        getUniqueValues={getUniqueValues}
        selectedStatuses={selectedStatuses}
        onStatusChange={handleStatusChange}
        onRefresh={fetchEmployees}
        isIntegrated={isIntegrated}
        userRole={userRole}
      />

      <div className="the-bench-layout">
        <div className="bench-main-content">
          {viewMode === "table" ? (
            <WorkflowTable
              employees={sortedData}
              allEmployees={employees}
              onSort={handleSort}
              sortColumn={sortColumn}
              sortOrder={sortOrder}
              selectedEmployees={selectedEmployees}
              onCheckboxChange={handleCheckboxChange}
              onEmployeeSelect={handleEmployeeSelect}
              columnFilters={columnFilters}
              onColumnFilterChange={handleColumnFilterChange}
            />
          ) : (
            <WorkflowKanban
              employees={filteredEmployees}
              onEmployeeSelect={handleEmployeeSelect}
            />
          )}
        </div>

        <div className={`bench-details-content ${selectedEmployee ? "open" : ""}`}>
          <EmployeeDetails
            employee={selectedEmployee}
            onClose={() => setSelectedEmployee(null)}
          />
        </div>
      </div>
    </div>
  );
}

export default TheBench;